import { useState } from "react";
import { useT } from "../i18n/hooks.ts";
import { RenameDialog } from "./RenameDialog.tsx";

/**
 * Persistent notice (not a one-off dialog) while working on an example
 * (ephemeral, see ProjectMeta.ephemeral in storage.ts): changes don't survive
 * unless a copy is saved — and the most common way to lose them is just
 * closing the tab, not pressing "Close project", so the notice has to be
 * visible the whole time you're working, not only on close.
 */
export function ExampleBanner({
  projectName,
  onSaveAsCopy,
  aboveDrawingBar = false,
}: {
  projectName: string;
  onSaveAsCopy: (name: string) => void;
  /**
   * True while a hand-drawing tool is active (SubBasinMap.tsx's own
   * `.map-drawing-bar`) — that bar sits in this exact same bottom-center
   * spot, same z-index 1000, and being the later of the two in the DOM this
   * banner would otherwise sit on top and swallow every click meant for the
   * bar's "Finalizar"/"Cancelar" buttons underneath it. Shifts this banner
   * up above the drawing bar instead of moving it somewhere else entirely
   * (e.g. next to the base-layer switcher, like MitecoAemetBanner.tsx did
   * for its own overlap) — it's still visible together with the drawing
   * bar, just stacked, and nothing else uses this exact spot to newly
   * collide with.
   */
  aboveDrawingBar?: boolean;
}) {
  const t = useT();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <div className={`ephemeral-banner${aboveDrawingBar ? " ephemeral-banner-sobre-dibujo" : ""}`}>
        <span>{t("exampleBanner.warning")}</span>
        <button className="button button-primary" onClick={() => setDialogOpen(true)}>
          {t("topbar.saveCopyAsButton")}
        </button>
      </div>

      {dialogOpen && (
        <RenameDialog
          title={t("topbar.saveAsDialogTitle")}
          initialName={t("topbar.copyNameSuffix", { name: projectName })}
          onSave={(name) => {
            onSaveAsCopy(name);
            setDialogOpen(false);
          }}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
}
